/**
 * In-page category filter for the All Products grid.
 *
 * The category chips in sections/lc-collection-header carry a filter key (data-lc-cat); every
 * product card <li> in Horizon's main-collection grid carries the keys it belongs to
 * (data-lc-cats, from snippets/lc-product-categories). Clicking a chip hides the non-matching
 * cards in place, marks the chip active and mirrors the state in the URL as ?category=<key>
 * (pushState, so Back/Forward and shared links work). "All" (an empty key) shows everything and
 * removes the parameter. Nothing is re-rendered: prices, stock state, swatches and links stay
 * exactly as Horizon rendered them.
 */
const PARAM = 'category';
const HIDDEN_CLASS = 'lc-cat-hidden';

class LcCollectionFilter extends HTMLElement {
  /** @type {HTMLAnchorElement[]} */
  chips = [];

  connectedCallback() {
    this.chips = Array.from(this.querySelectorAll('[data-lc-cat]'));
    if (!this.chips.length) return;
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);
    this.apply(this.#keyFromUrl(), false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#onPopState);
  }

  get keys() {
    return this.chips.map((c) => c.dataset.lcCat || '').filter(Boolean);
  }

  /** @returns {HTMLElement[]} */
  get cards() {
    return Array.from(document.querySelectorAll('.product-grid__item[data-lc-cats]'));
  }

  /** A category from the URL is only honoured when a chip offers it; anything else means All. */
  #keyFromUrl() {
    const key = new URLSearchParams(location.search).get(PARAM) || '';
    return this.keys.includes(key) ? key : '';
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-lc-cat]') : null;
    if (!(chip instanceof HTMLElement) || !this.contains(chip)) return;
    event.preventDefault();
    this.apply(chip.dataset.lcCat || '', true);
  };

  #onPopState = () => this.apply(this.#keyFromUrl(), false);

  /**
   * @param {string} key - filter key, '' for All
   * @param {boolean} push - add a history entry and update the URL
   */
  apply(key, push) {
    const cards = this.cards;
    let visible = 0;
    for (const card of cards) {
      const cats = (card.dataset.lcCats || '').split(/\s+/).filter(Boolean);
      const show = !key || cats.includes(key);
      card.classList.toggle(HIDDEN_CLASS, !show);
      if (show) visible += 1;
    }

    for (const chip of this.chips) {
      const on = (chip.dataset.lcCat || '') === key;
      chip.classList.toggle('lc-chead__cat--on', on);
      chip.setAttribute('aria-pressed', String(on));
      // On phones the chip row scrolls sideways: keep the active chip in view (row only, never the page).
      const row = chip.parentElement;
      if (on && row instanceof HTMLElement && row.scrollWidth > row.clientWidth) {
        row.scrollTo({ left: chip.offsetLeft - (row.clientWidth - chip.offsetWidth) / 2, behavior: 'smooth' });
      }
    }

    this.#renderCounts(visible, cards.length, key);

    if (push) {
      const url = new URL(location.href);
      if (key) url.searchParams.set(PARAM, key);
      else url.searchParams.delete(PARAM);
      if (url.href !== location.href) history.pushState({ lcCategory: key }, '', url);
    }
  }

  /**
   * @param {number} visible
   * @param {number} total
   * @param {string} key
   */
  #renderCounts(visible, total, key) {
    const count = this.querySelector('[data-lc-count]');
    if (count instanceof HTMLElement) {
      count.textContent = `${visible} ${visible === 1 ? 'product' : 'products'}`;
    }

    const note = document.querySelector('[data-lc-results-note]');
    if (note instanceof HTMLElement) {
      const all = note.dataset.lcTextAll || '';
      const filtered = note.dataset.lcTextFiltered || all;
      const empty = note.dataset.lcTextEmpty || filtered;
      const template = !key ? all : visible === 0 ? empty : filtered;
      note.textContent = template.replace('[count]', String(visible)).replace('[total]', String(total));
    }
  }
}

if (!customElements.get('lc-collection-filter')) customElements.define('lc-collection-filter', LcCollectionFilter);
